// Shared chunked-upload engine — calls the SAME existing backend endpoints
// (/api/upload/init, /api/upload/chunk, /api/upload/status, /api/upload/finalize)
// as ContentStudio.jsx's Media Library uploader. This is the one and only
// upload pipeline; this hook does not add a second one, it only gives a
// second frontend surface (the Phase 2 Project Upload page) a reusable way
// to drive the exact same backend calls with a project_id attached,
// without touching ContentStudio.jsx itself (zero regression risk to the
// existing Media Library).
const API_URL = window.__BACKEND_URL;
const CHUNK_SIZE = 5 * 1024 * 1024;
const CONCURRENCY = /Mobi|Android/i.test(navigator.userAgent) ? 4 : 6;
const MAX_RETRIES = 3;
const UPLOAD_LS_PREFIX = 'proj_upload_';

const makeFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;
const getUploadSession = (fileKey) => {
  try { return JSON.parse(localStorage.getItem(UPLOAD_LS_PREFIX + fileKey)); } catch { return null; }
};
const saveUploadSession = (fileKey, data) => {
  try { localStorage.setItem(UPLOAD_LS_PREFIX + fileKey, JSON.stringify(data)); } catch { /* ignore */ }
};
const clearUploadSession = (fileKey) => {
  try { localStorage.removeItem(UPLOAD_LS_PREFIX + fileKey); } catch { /* ignore */ }
};

const sendRequest = (url, body, options = {}) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.withCredentials = true;
  xhr.timeout = 120000;
  xhr.addEventListener('load', () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
    } else {
      let detail = 'Request failed';
      try {
        const parsed = JSON.parse(xhr.responseText);
        detail = typeof parsed?.detail === 'string' ? parsed.detail : (parsed?.detail?.message || detail);
      } catch { /* keep default detail */ }
      const err = new Error(detail);
      err.statusCode = xhr.status;
      reject(err);
    }
  });
  xhr.addEventListener('error', () => reject(new Error('Network error')));
  xhr.addEventListener('timeout', () => reject(new Error('Request timed out')));
  xhr.open(options.method || 'POST', url);
  if (options.headers) Object.entries(options.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
  xhr.send(body);
});

const sendChunkWithRetry = async (uploadId, chunkIndex, blob, retries = MAX_RETRIES) => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const form = new FormData();
      form.append('upload_id', uploadId);
      form.append('chunk_index', chunkIndex);
      form.append('file', blob, `chunk_${chunkIndex}`);
      return await sendRequest(`${API_URL}/api/upload/chunk`, form);
    } catch (err) {
      if (attempt === retries - 1) throw err;
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
    }
  }
};

const recoverFromFinalize = async (uploadId) => {
  try {
    const statusResp = await fetch(`${API_URL}/api/upload/status/${uploadId}`, { credentials: 'include' });
    if (!statusResp.ok) return null;
    const session = await statusResp.json();
    if (session.status === 'completed' && session.content_source_id) {
      const assetResp = await fetch(`${API_URL}/api/admin/content-sources`, { credentials: 'include' });
      if (!assetResp.ok) return null;
      const sources = await assetResp.json();
      return sources.find((s) => s.id === session.content_source_id) || null;
    }
  } catch { /* server unreachable */ }
  return null;
};

/**
 * Upload one file through the existing chunked pipeline, optionally tagging
 * the new Parent Asset with a Content Project (project_id) and/or grouping
 * it as a Child Asset (parentAssetId) — the two are mutually exclusive, same
 * rule enforced server-side in Phase 1.
 */
export async function uploadFileWithProgress(file, { creatorId, projectId = null, parentAssetId = null, brandId = null, onProgress } = {}) {
  const fileKey = makeFileKey(file);
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let completedChunks = 0;
  const completedSet = new Set();
  const report = (force) => {
    if (onProgress) onProgress(Math.round((completedChunks / totalChunks) * 100), force);
  };

  let uploadId = null;
  const existing = getUploadSession(fileKey);
  let resuming = false;

  if (existing?.uploadId) {
    try {
      const sResp = await fetch(`${API_URL}/api/upload/status/${existing.uploadId}`, { credentials: 'include' });
      if (sResp.ok) {
        const sData = await sResp.json();
        if (sData.status === 'completed') {
          const recovered = await recoverFromFinalize(existing.uploadId);
          if (recovered) {
            clearUploadSession(fileKey);
            return recovered;
          }
        }
        if (sData.status === 'in_progress' && sData.total_chunks === totalChunks) {
          uploadId = existing.uploadId;
          resuming = true;
          (sData.completed_chunks || []).forEach((i) => completedSet.add(i));
          completedChunks = completedSet.size;
          report(true);
        }
      }
    } catch { /* start fresh below */ }
  }

  if (!uploadId) {
    clearUploadSession(fileKey);
    const initForm = new FormData();
    initForm.append('filename', file.name);
    initForm.append('total_chunks', totalChunks);
    initForm.append('total_size', file.size);
    initForm.append('creator_id', creatorId);
    if (brandId) initForm.append('brand_id', brandId);
    initForm.append('title', file.name);
    initForm.append('description', 'Uploaded from Project Upload');
    initForm.append('content_type', file.type?.startsWith('image/') ? 'image' : 'long_form');
    if (parentAssetId) initForm.append('parent_asset_id', parentAssetId);
    if (projectId) initForm.append('project_id', projectId);

    const initResp = await sendRequest(`${API_URL}/api/upload/init`, initForm);
    uploadId = initResp.upload_id;
    if (!uploadId) throw new Error('Failed to initialize upload');
  }

  saveUploadSession(fileKey, { uploadId, fileKey, totalChunks, filename: file.name, fileSize: file.size });

  const pendingChunks = [];
  for (let i = 0; i < totalChunks; i++) {
    if (!completedSet.has(i)) pendingChunks.push(i);
  }
  if (!(resuming && pendingChunks.length === 0)) {
    let cursor = 0;
    const uploadNext = async () => {
      while (cursor < pendingChunks.length) {
        const idx = pendingChunks[cursor++];
        const start = idx * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        await sendChunkWithRetry(uploadId, idx, file.slice(start, end));
        completedSet.add(idx);
        completedChunks = completedSet.size;
        report();
      }
    };
    const workers = Array.from({ length: Math.min(CONCURRENCY, pendingChunks.length || 1) }, () => uploadNext());
    await Promise.all(workers);
  }
  completedChunks = totalChunks;
  report(true);

  try {
    const created = await sendRequest(`${API_URL}/api/upload/finalize`, JSON.stringify({ upload_id: uploadId }), { headers: { 'Content-Type': 'application/json' } });
    clearUploadSession(fileKey);
    return created;
  } catch (finalizeErr) {
    const recovered = await recoverFromFinalize(uploadId);
    if (recovered) {
      clearUploadSession(fileKey);
      return recovered;
    }
    throw finalizeErr;
  }
}
